// Resumable recording uploader.
//
// Two entry points:
//   - `pumpSession(sessionId)`: called by the recorder after each chunk lands
//     in IDB. Best-effort, idempotent.
//   - `resumeOrphans()`: called once at app boot. Walks IDB for any sessions
//     that still have un-uploaded chunks (from a previous tab/crash) and
//     pushes them now.
//
// Per-call addressing: each chunk carries (sessionId, callId, seq). One
// session may contain many calls; chunks are uploaded to a call-scoped
// route so the server can list/play/delete each call independently.

import type { SessionId } from '@prakash-software/shared';
import { useAgentStore } from '../../stores/agent';
import { useRecordingsStore } from '../../stores/recordings';
import { listChunks, listOrphanSessions, markUploaded, type ChunkRow } from './idb';

const inFlight = new Set<string>();

function serverBase(): string {
  const explicit = import.meta.env.VITE_AUTH_SERVER_URL as string | undefined;
  if (explicit && explicit !== 'auto') return explicit.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const { protocol, hostname, port } = window.location;
    if (port && port !== '80' && port !== '443') return `${protocol}//${hostname}:4000`;
    return `${protocol}//${hostname}`;
  }
  return 'http://localhost:4000';
}

function authHeader(): Record<string, string> {
  const jwt = useAgentStore.getState().tokens?.accessToken;
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

interface KnownChunk {
  callId: string;
  seq: number;
}

/** Fetch the set of (callId, seq) the server already has for this session.
 *  Used to skip uploads that already landed (e.g. retried IDB chunks). */
async function knownChunks(sessionId: string): Promise<Set<string>> {
  const res = await fetch(
    `${serverBase()}/api/recordings/${encodeURIComponent(sessionId)}/chunks`,
    { headers: { ...authHeader() } },
  );
  if (!res.ok) return new Set();
  const json = (await res.json()) as { chunks?: KnownChunk[] };
  return new Set((json.chunks ?? []).map((c) => `${c.callId}:${c.seq}`));
}

async function uploadOne(row: ChunkRow): Promise<boolean> {
  const url =
    `${serverBase()}/api/recordings/${encodeURIComponent(row.sessionId)}` +
    `/calls/${encodeURIComponent(row.callId)}/chunks/${row.seq}`;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/octet-stream',
        // The server uses chunk-0's started_at as the canonical call start
        // for sorting + duration. Sending it on every chunk is harmless
        // (server only writes it on the first insert per call).
        'X-Call-Started-At': String(row.callStartedAt),
        ...authHeader(),
      },
      body: row.blob,
    });
    if (res.status === 204) {
      await markUploaded(row.sessionId, row.callId, row.seq);
      // Reflect server-known state in the local recordings cache so the
      // monitor indicator + Conversation panel appear immediately.
      useRecordingsStore.getState().add(row.sessionId as SessionId);
      return true;
    }
    // 401/403 — wait for the agent to re-auth; surface for diagnostics.
     
    console.warn('[recording] upload non-204', row.sessionId, row.callId, row.seq, res.status);
    return false;
  } catch (err) {
    // Network blip — caller retries via pumpSession on the next tick.
     
    console.warn('[recording] upload failed', row.sessionId, row.callId, row.seq, err);
    return false;
  }
}

/**
 * Push every un-uploaded chunk for `sessionId` to the server. Cheap to call
 * repeatedly — concurrent calls for the same session deduplicate.
 */
export async function pumpSession(sessionId: string): Promise<void> {
  if (inFlight.has(sessionId)) return;
  inFlight.add(sessionId);
  try {
    const [chunks, known] = await Promise.all([
      listChunks(sessionId),
      knownChunks(sessionId),
    ]);
    for (const row of chunks) {
      if (row.uploaded) continue;
      const key = `${row.callId}:${row.seq}`;
      if (known.has(key)) {
        await markUploaded(sessionId, row.callId, row.seq);
        continue;
      }
      const ok = await uploadOne(row);
      if (!ok) break; // bail on first failure; next pump retries.
    }
  } finally {
    inFlight.delete(sessionId);
  }
}

/**
 * On agent-app boot: any sessions with leftover chunks in IDB (from a
 * previous tab or crash) get pumped. Failures retry on the next call.
 */
export async function resumeOrphans(): Promise<void> {
  const sessions = await listOrphanSessions();
  await Promise.all(sessions.map((s) => pumpSession(s)));
}

/**
 * Best-effort thumbnail upload — the agent recorder captures one early in
 * the call (a couple seconds after start) so the agent's Recording panel
 * can show a preview tile per call. Single shot; failure is silent.
 */
export async function uploadThumbnail(
  sessionId: string,
  callId: string,
  blob: Blob,
): Promise<void> {
  const url =
    `${serverBase()}/api/recordings/${encodeURIComponent(sessionId)}` +
    `/calls/${encodeURIComponent(callId)}/thumbnail`;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': blob.type || 'image/jpeg', ...authHeader() },
      body: blob,
    });
    if (res.status !== 204) {
       
      console.warn('[recording] thumbnail upload non-204', res.status);
    }
  } catch (err) {
     
    console.warn('[recording] thumbnail upload failed', err);
  }
}
